import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EmptyStateComponent } from '../../../../../components/empty-state/empty-state.component';
import {
  IPrefillModalResult,
  PrefillModalComponent,
} from '../../../../../components/prefill-modal/prefill-modal.component';
import {
  IPrefillValueModalResult,
  PrefillValueModalComponent,
} from '../../../../../components/prefill-value-modal/prefill-value-modal.component';
import { SkeletonComponent } from '../../../../../components/skeleton/skeleton.component';
import { FieldTypeModel } from '../../../../../models/field-type.model';
import { PrefillValueModel } from '../../../../../models/prefill-value.model';
import { TemplateFieldModel } from '../../../../../models/template-field.model';
import { TemplateModel } from '../../../../../models/template.model';
import { DateToMomentPipe } from '../../../../../pipes/date-to-moment.pipe';
import { ConfirmService } from '../../../../../services/confirm.service';
import { FieldTypeService } from '../../../../../services/field-type.service';
import { PrefillService } from '../../../../../services/prefill.service';
import { TemplateService } from '../../../../../services/template.service';

/** Marca de enmascarado de los valores protegidos. */
const MASK = '••••••••••';

/**
 * Detalle de un prerellenado y CRUD de sus valores.
 *
 * Los valores llegan dentro de `prefill.values`, cada uno con su
 * `templateField`. Para saber que campos quedan libres hace falta la plantilla
 * completa, y para saber si un valor es protegido, el catalogo de tipos.
 *
 * DOS CUIDADOS:
 *  · El API no impide dos valores para el mismo campo: el alta solo ofrece los
 *    campos que aun no tienen valor.
 *  · Borrar un valor es FISICO, no soft-delete.
 */
@Component({
  selector: 'app-prefill-page',
  imports: [DateToMomentPipe, EmptyStateComponent, RouterLink, SkeletonComponent],
  templateUrl: './prefill-page.component.html',
  styleUrl: './prefill-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrefillPageComponent {
  private readonly prefillService = inject(PrefillService);
  private readonly templateService = inject(TemplateService);
  private readonly fieldTypeService = inject(FieldTypeService);
  private readonly modalService = inject(NgbModal);
  private readonly confirmService = inject(ConfirmService);

  /** Llega del router gracias a `withComponentInputBinding()`. */
  readonly prefillId = input.required<string>();

  protected readonly prefill = this.prefillService.prefill;

  /**
   * `true` cuando la PRIMERA carga ya termino, con exito o con error. Es lo
   * que decide si se pueden pintar estados vacios: `loading` no sirve para
   * eso porque arranca en `false` y solo se enciende cuando arranca la
   * peticion, de modo que el primer frame pintaria «no encontrado» y se
   * veria un parpadeo.
   *
   * Vive en el componente, no en el servicio (que es un singleton): asi
   * vuelve a `false` al abrir otro prerellenado.
   */
  protected readonly loaded = signal(false);

  /**
   * Mensaje del fallo de carga, o `null`. Distingue «no se pudo cargar»
   * (fallo del API) de «el prerellenado no existe»: son dos cosas distintas
   * y el usuario necesita saber cual es.
   */
  protected readonly error = this.prefillService.prefillError;

  /** Plantilla del prerellenado: aporta la lista completa de campos. */
  protected readonly template = signal<TemplateModel | null>(null);

  /** Catalogo completo de tipos: dice cuales son protegidos o multilinea. */
  protected readonly fieldTypes = signal<FieldTypeModel[]>([]);

  /** Valores revelados en pantalla, por `prefillValueId`. */
  protected readonly revealed = signal<ReadonlySet<string>>(new Set<string>());

  private readonly fieldTypeMap = computed(() => {
    const map = new Map<string, FieldTypeModel>();
    for (const fieldType of this.fieldTypes()) map.set(fieldType.fieldType, fieldType);
    return map;
  });

  /** Valores en el mismo orden en que la plantilla pinta sus campos. */
  protected readonly values = computed(() =>
    [...(this.prefill()?.values ?? [])].sort(
      (a, b) =>
        (a.templateField?.position ?? 0) - (b.templateField?.position ?? 0) ||
        (a.templateField?.label ?? '').localeCompare(b.templateField?.label ?? ''),
    ),
  );

  /**
   * Campos ACTIVOS de la plantilla que todavia no tienen valor. Es la unica
   * defensa contra los duplicados: el API no valida nada.
   */
  protected readonly availableFields = computed(() => {
    const used = new Set(this.values().map((value) => value.templateFieldId));
    return (this.template()?.fields ?? [])
      .filter((field) => field.status && !used.has(field.templateFieldId))
      .sort((a, b) => a.position - b.position);
  });

  protected readonly templateName = computed(() => {
    const template = this.template();
    return template ? `${template.name} (${template.code})` : 'Plantilla no disponible';
  });

  constructor() {
    effect(() => {
      const prefillId = this.prefillId();
      void this.load(prefillId);
    });
  }

  /** Ultimo prerellenado pedido: distingue «otro prerellenado» de «recarga». */
  private lastRequestedId: string | null = null;

  private async load(prefillId: string): Promise<void> {
    // Si se abre OTRO prerellenado, lo que hay en pantalla ya no es de esta
    // ficha: se vuelve a esqueletos. Una recarga del MISMO prerellenado no
    // los pinta, para que crear o editar un valor no haga desaparecer la
    // ficha entera.
    if (this.lastRequestedId !== prefillId) {
      this.lastRequestedId = prefillId;
      this.loaded.set(false);
    }

    try {
      const prefill = await this.prefillService.getPrefill(prefillId);
      this.revealed.set(new Set<string>());

      if (!prefill) {
        this.template.set(null);
        return;
      }

      const [template, fieldTypes] = await Promise.all([
        this.templateService.getTemplate(prefill.templateId),
        this.fieldTypeService.getFieldTypes(),
      ]);

      this.template.set(template);
      this.fieldTypes.set(fieldTypes);
    } finally {
      // Tambien tras un fallo: lo que apaga los esqueletos es «ya se
      // intento», no «salio bien». El error se cuenta aparte, con `error()`.
      this.loaded.set(true);
    }
  }

  protected async reload(): Promise<void> {
    await this.load(this.prefillId());
  }

  // ---------------------------------------------------------------------------
  // Valores protegidos
  // ---------------------------------------------------------------------------

  protected fieldTypeOf(value: PrefillValueModel): FieldTypeModel | null {
    const key = value.templateField?.fieldType ?? '';
    return this.fieldTypeMap().get(key) ?? null;
  }

  protected isProtected(value: PrefillValueModel): boolean {
    return this.fieldTypeOf(value)?.protected === true;
  }

  protected isRevealed(value: PrefillValueModel): boolean {
    return this.revealed().has(value.prefillValueId);
  }

  /** Enmascara el valor mientras no se pida revelarlo. */
  protected displayValue(value: PrefillValueModel): string {
    if (!this.isProtected(value) || this.isRevealed(value)) return value.value;
    return MASK;
  }

  protected toggleReveal(value: PrefillValueModel): void {
    this.revealed.update((current) => {
      const next = new Set(current);
      if (next.has(value.prefillValueId)) next.delete(value.prefillValueId);
      else next.add(value.prefillValueId);
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Prerellenado
  // ---------------------------------------------------------------------------

  protected async edit(): Promise<void> {
    const prefill = this.prefill();
    if (!prefill) return;

    const reference = this.modalService.open(PrefillModalComponent, {
      centered: true,
      backdrop: 'static',
      scrollable: true,
      ariaLabelledBy: 'ks-prefill-modal-title',
    });

    const template = this.template();

    (reference.componentInstance as PrefillModalComponent).init(prefill, template ? [template] : []);

    let result: IPrefillModalResult;

    try {
      result = (await reference.result) as IPrefillModalResult;
    } catch {
      return;
    }

    const updated = await this.prefillService.updatePrefill(prefill.prefillId, {
      name: result.name,
      status: result.status,
      contentType: result.contentType,
    });

    if (!updated) return;

    if (result.file && updated.presignFile) {
      await this.prefillService.uploadFile(updated.presignFile, result.file);
    }

    await this.reload();
  }

  /** Borrado SOFT: el prerellenado se desactiva, no desaparece. */
  protected async deactivate(): Promise<void> {
    const prefill = this.prefill();
    if (!prefill) return;

    const confirmed = await this.confirmService.askDeactivate(
      'el prerellenado',
      `${prefill.name} · ${this.templateName()}`,
    );

    if (!confirmed) return;

    const deleted = await this.prefillService.deletePrefill(prefill.prefillId);
    if (deleted) await this.reload();
  }

  protected async activate(): Promise<void> {
    const prefill = this.prefill();
    if (!prefill) return;

    const updated = await this.prefillService.updatePrefill(prefill.prefillId, {
      name: prefill.name,
      status: true,
      contentType: null,
    });

    if (updated) await this.reload();
  }

  // ---------------------------------------------------------------------------
  // Valores
  // ---------------------------------------------------------------------------

  protected async createValue(): Promise<void> {
    const prefill = this.prefill();
    if (!prefill) return;

    const result = await this.openValueModal(null, this.availableFields());
    if (!result) return;

    const created = await this.prefillService.createPrefillValue(prefill.prefillId, {
      templateFieldId: result.templateFieldId,
      value: result.value,
    });

    if (created) await this.reload();
  }

  protected async editValue(value: PrefillValueModel): Promise<void> {
    const prefill = this.prefill();
    if (!prefill) return;

    const field =
      value.templateField ??
      (this.template()?.fields ?? []).find(
        (item) => item.templateFieldId === value.templateFieldId,
      ) ??
      null;

    const result = await this.openValueModal(value, field ? [field] : []);
    if (!result) return;

    // El PUT del API solo acepta `value`.
    const updated = await this.prefillService.updatePrefillValue(
      prefill.prefillId,
      value.prefillValueId,
      { value: result.value },
    );

    if (updated) await this.reload();
  }

  /**
   * OJO: aqui el borrado es FISICO. La confirmacion lo dice sin rodeos, a
   * diferencia del resto del modulo, donde DELETE solo desactiva.
   */
  protected async deleteValue(value: PrefillValueModel): Promise<void> {
    const prefill = this.prefill();
    if (!prefill) return;

    const label = value.templateField?.label ?? 'el campo';

    const confirmed = await this.confirmService.askDelete(
      'este valor',
      `${label} · ${prefill.name}`,
    );

    if (!confirmed) return;

    const deleted = await this.prefillService.deletePrefillValue(
      prefill.prefillId,
      value.prefillValueId,
    );

    if (deleted) await this.reload();
  }

  private async openValueModal(
    value: PrefillValueModel | null,
    fields: TemplateFieldModel[],
  ): Promise<IPrefillValueModalResult | null> {
    const reference = this.modalService.open(PrefillValueModalComponent, {
      centered: true,
      backdrop: 'static',
      ariaLabelledBy: 'ks-prefill-value-modal-title',
    });

    (reference.componentInstance as PrefillValueModalComponent).init(
      value,
      fields,
      this.fieldTypes(),
    );

    try {
      return (await reference.result) as IPrefillValueModalResult;
    } catch {
      return null;
    }
  }
}
